import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, Plus, Edit, Trash2, X, Check, Clock, Users, Key, AlertCircle
} from 'lucide-react';
import { apiService, Role, Permission } from '../../services/api';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface RoleFormData {
  name: string;
  description: string;
  context: 'backoffice' | 'dealership';
  permissionIds: string[];
}

const emptyForm: RoleFormData = {
  name: '',
  description: '',
  context: 'dealership',
  permissionIds: [],
};

export const RolesManagementPage: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<RoleFormData>(emptyForm);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [rolesData, permissionsData] = await Promise.all([
        apiService.getRoles(),
        apiService.getPermissions(),
      ]);

      setRoles(Array.isArray(rolesData) ? rolesData : []);
      setPermissions(Array.isArray(permissionsData) ? permissionsData : []);
    } catch (err: any) {
      setError('Erro ao carregar papéis e permissões');
      console.error('Erro ao carregar RBAC:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (role: Role) => {
    setFormData({
      name: role.name,
      description: role.description || '',
      context: role.context,
      permissionIds: role.permissions.map(rp => rp.permissionId),
    });
    setEditingId(role.id);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setToast({ message: 'Nome é obrigatório', type: 'error' });
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        context: formData.context,
        permissionIds: formData.permissionIds,
      };

      if (editingId) {
        await apiService.updateRole(editingId, payload);
        setToast({ message: 'Papel atualizado com sucesso!', type: 'success' });
      } else {
        await apiService.createRole(payload);
        setToast({ message: 'Papel criado com sucesso!', type: 'success' });
      }

      await load();
      resetForm();
    } catch (err: any) {
      setToast({ message: err.message || 'Erro ao salvar papel', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Tem certeza que deseja excluir o papel "${name}"?`)) return;

    try {
      setSaving(true);
      await apiService.deleteRole(id);
      await load();
      setToast({ message: 'Papel excluído com sucesso!', type: 'success' });
    } catch (err: any) {
      setToast({ message: err.message || 'Erro ao excluir papel', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const togglePermission = (permissionId: string) => {
    setFormData(prev => ({
      ...prev,
      permissionIds: prev.permissionIds.includes(permissionId)
        ? prev.permissionIds.filter(id => id !== permissionId)
        : [...prev.permissionIds, permissionId],
    }));
  };

  // Group permissions by resource
  const groupedPermissions = permissions.reduce((acc, permission) => {
    if (!acc[permission.resource]) {
      acc[permission.resource] = [];
    }
    acc[permission.resource].push(permission);
    return acc;
  }, {} as Record<string, Permission[]>);

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top">
          <div className={`px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
            toast.type === 'success' ? 'bg-green-600 text-white' :
            toast.type === 'error' ? 'bg-red-600 text-white' :
            'bg-blue-600 text-white'
          }`}>
            {toast.type === 'success' && <Check className="h-5 w-5" />}
            {toast.type === 'error' && <X className="h-5 w-5" />}
            <span>{toast.message}</span>
            <button onClick={() => setToast(null)} className="ml-2">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Shield className="h-6 w-6" />
            Gerenciamento de Papéis e Permissões
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Configure papéis de usuários e suas permissões
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 flex items-center gap-2 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Novo Papel
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card p-4 rounded-lg border border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total de Papéis</p>
              <p className="text-2xl font-bold text-foreground">{roles.length}</p>
            </div>
            <Shield className="h-8 w-8 text-accent-600" />
          </div>
        </div>

        <div className="bg-card p-4 rounded-lg border border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Permissões</p>
              <p className="text-2xl font-bold text-foreground">{permissions.length}</p>
            </div>
            <Key className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-card p-4 rounded-lg border border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Usuários com Papéis</p>
              <p className="text-2xl font-bold text-foreground">
                {roles.reduce((sum, role) => sum + (role._count?.userRoles || 0), 0)}
              </p>
            </div>
            <Users className="h-8 w-8 text-green-600" />
          </div>
        </div>
      </div>

      {/* Roles List */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Papéis Configurados</h2>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <Clock className="h-8 w-8 animate-spin mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">Carregando...</p>
          </div>
        ) : roles.length === 0 ? (
          <div className="p-8 text-center">
            <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">Nenhum papel configurado</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-accent/5">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Nome</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Contexto</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Permissões</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Usuários</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {roles.map((role) => (
                <tr key={role.id} className="hover:bg-accent/5">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-foreground flex items-center gap-2">
                        {role.name}
                        {role.isSystem && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">Sistema</span>
                        )}
                      </p>
                      {role.description && (
                        <p className="text-xs text-muted-foreground line-clamp-1">{role.description}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      role.context === 'backoffice'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {role.context === 'backoffice' ? 'Backoffice' : 'Concessionária'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {role.permissions.length} permissões
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {role._count?.userRoles || 0} usuários
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      role.isActive
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {role.isActive ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(role)}
                        disabled={saving}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      {!role.isSystem && (
                        <button
                          onClick={() => handleDelete(role.id, role.name)}
                          disabled={saving}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">
                {editingId ? 'Editar Papel' : 'Novo Papel'}
              </h2>
              <button onClick={resetForm} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Nome <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-600"
                    required
                    placeholder="ex: manager, salesperson"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Contexto <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.context}
                    onChange={(e) => setFormData(prev => ({ ...prev, context: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-600"
                    disabled={!!editingId}
                    required
                  >
                    <option value="dealership">Concessionária</option>
                    <option value="backoffice">Backoffice</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-600"
                  rows={2}
                  placeholder="Descreva as responsabilidades deste papel..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-3">Permissões</label>
                <div className="border border-border rounded-lg p-4 max-h-96 overflow-y-auto space-y-4">
                  {Object.entries(groupedPermissions).map(([resource, perms]) => (
                    <div key={resource} className="space-y-2">
                      <p className="text-sm font-semibold text-foreground capitalize">{resource}</p>
                      <div className="grid grid-cols-2 gap-2">
                        {perms.map((permission) => (
                          <label
                            key={permission.id}
                            className="flex items-start gap-2 p-2 rounded hover:bg-accent/5 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={formData.permissionIds.includes(permission.id)}
                              onChange={() => togglePermission(permission.id)}
                              className="mt-0.5 rounded"
                            />
                            <div className="flex-1">
                              <p className="text-sm text-foreground">{permission.name}</p>
                              {permission.description && (
                                <p className="text-xs text-muted-foreground">{permission.description}</p>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}

                  {Object.keys(groupedPermissions).length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Nenhuma permissão disponível
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3 pt-4 border-t border-border">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  disabled={saving}
                  className="px-4 py-2 border border-border rounded-lg hover:bg-accent/5 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
